// apolo/src/lib/loop/condenser.js — harness-uplift-r2 E2 (condenser).
// @arch:pure-engine
//
// OpenHands-style condensation. The raw event log is APPEND-ONLY and NEVER
// mutated here; buildView returns a rebuilt model-facing PROJECTION plus a
// Condensation record {forgottenIds, summary, offset}. Recent events are kept
// verbatim; only OLDER events are condensed into a single summary row.
//
// Dual trigger (the caller decides which to use):
//   - threshold prevention: needsCondense(events, {budget, sizeOf}) → boolean
//   - overflow recovery:    buildView(events, {force: true}) — condense regardless
//
// summarizeFn is INJECTED (multi-LLM LAW — never hardcode a model). When absent
// OR when it throws, we fall back to a DETERMINISTIC digest — so this never adds
// an LLM dependency to a turn and never breaks one.
//
// Params are REPROJECTED: the OpenHands LLMSummarizingCondenser numeric defaults
// (max_size=120, keep_first=4) were REFUTED 0-3 in the audit — not copied.

// Reprojected default: keep the most recent N events verbatim. Chosen > 4 on
// purpose (the refuted value was 4); tune via A/B (E7) before any default-ON.
export const DEFAULT_KEEP_RECENT = 8;

const SUMMARY_HEADER = 'Contexto condensado (eventos anteriores — use como memória do que já foi feito):';

function isEvent(m) {
  return m && typeof m === 'object';
}

/** Deterministic fallback digest of the older events (no LLM). Never throws. */
function deterministicSummary(olderEvents) {
  try {
    const lines = [];
    for (const m of olderEvents) {
      if (!isEvent(m)) continue;
      const role = typeof m.role === 'string' ? m.role : 'msg';
      const content = typeof m.content === 'string' ? m.content : '';
      if (!content.trim()) continue;
      const clipped = content.length > 200 ? `${content.slice(0, 200)}…` : content;
      lines.push(`[${role}] ${clipped}`);
    }
    return lines.join('\n');
  } catch {
    return '';
  }
}

/**
 * Rebuild the model-facing View from an append-only event log.
 *
 * @param {Array<object>} events - the raw log (NEVER mutated)
 * @param {{ keepRecent?: number, summarizeFn?: Function, force?: boolean }} opts
 * @returns {{ view: Array<object>, condensation: {forgottenIds:string[], summary:string, offset:number}|null }}
 */
export function buildView(events, opts = {}) {
  try {
    const log = Array.isArray(events) ? events : [];
    const keepRecent = Number.isFinite(opts.keepRecent) ? opts.keepRecent : DEFAULT_KEEP_RECENT;
    const force = opts.force === true;

    // Passthrough when short enough and not forced. Return a shallow copy so the
    // caller can never accidentally alias-mutate our return into the raw log.
    if (!force && log.length <= keepRecent) {
      return { view: log.slice(), condensation: null };
    }

    // Split: older (condensed) vs recent (verbatim). When forced on a short log,
    // condense everything but the last event so there is always something to keep.
    const cut = force && log.length <= keepRecent ? Math.max(0, log.length - 1) : log.length - keepRecent;
    const older = log.slice(0, cut);
    const recent = log.slice(cut);

    let summary = '';
    if (typeof opts.summarizeFn === 'function') {
      try {
        const s = opts.summarizeFn(older);
        summary = typeof s === 'string' ? s : '';
      } catch {
        summary = ''; // fall through to deterministic
      }
    }
    if (!summary) summary = deterministicSummary(older);

    const forgottenIds = older
      .filter(isEvent)
      .map((m) => (typeof m.id === 'string' || typeof m.id === 'number' ? m.id : null))
      .filter((id) => id !== null);

    const summaryRow = { role: 'user', content: `${SUMMARY_HEADER}\n${summary}`, _condensed: true };
    const view = [summaryRow, ...recent.filter(isEvent)];

    return { view, condensation: { forgottenIds, summary, offset: 0 } };
  } catch {
    return { view: Array.isArray(events) ? events.slice() : [], condensation: null };
  }
}

/**
 * Threshold trigger (prevention). True when the log's measured size crosses the
 * budget. `sizeOf` is injected (token/char estimator) so this stays pure.
 *
 * @param {Array<object>} events
 * @param {{ budget?: number|null, sizeOf?: (e:object)=>number }} opts
 * @returns {boolean}
 */
export function needsCondense(events, opts = {}) {
  try {
    const budget = opts.budget;
    if (!Number.isFinite(budget)) return false;
    const log = Array.isArray(events) ? events : [];
    const sizeOf = typeof opts.sizeOf === 'function' ? opts.sizeOf : (m) => (m?.content?.length || 0);
    let total = 0;
    for (const m of log) total += Number(sizeOf(m)) || 0;
    return total >= budget;
  } catch {
    return false;
  }
}
